import { Button, ButtonSizeType } from '@design-system';
import { isPast } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect } from 'react';

import { DEFAULT_CURRENCY_CODE } from '../../../constants/locations';
import { getProductRef } from '../../../helpers/purchasing';
import { useIsSubscribed } from '../../../hooks/subscription';
import { useAuthenticate } from '../../../hooks/useAuthenticate';
import { useIsPreOrder } from '../../../hooks/useIsPreOrder';
import { useAuth, useFlow } from '../../../providers';
import { usePreAuthDrawCtx } from '../../../providers/PreAuthDrawProvider';
import { usePurchase } from '../../../providers/PurchaseProvider';
import { useStore } from '../../../store/utils';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { buildSubscriptions } from '../../../utils/subscriptions';
import { AuthEntryPoint } from '../../auth/types';
import { useDrawButtonLabels } from '../hooks/useDrawButtonLabels';
import { DrawByProductId } from '../types';
import { usePreOrderFlow } from '../utils/usePreOrderFlow';

type Props = {
  buttonLocation: 'dialogCta' | 'mainCta' | 'stickyCta';
  draw: DrawByProductId;
  hasCheckoutParam?: boolean;
  product: ProductType;
  onClick?: () => void;
  size?: ButtonSizeType;
};

export const PreAuthDrawButton: React.FC<Props> = ({
  buttonLocation,
  draw,
  hasCheckoutParam = false,
  onClick,
  product,
  size = 'lg',
}) => {
  const { isLoggedIn } = useAuth();
  const { startFlow } = useFlow();
  const [emitter] = useAnalyticsEmitter();
  const { t } = useTranslation(['product', 'authentication']);
  const purchasingController = usePurchase();
  const productRef = getProductRef(purchasingController);
  const { authenticateAndSubscribe } = useAuthenticate();
  const isPreOrder = useIsPreOrder(draw);
  const flowConfig = usePreOrderFlow(draw, product, isPreOrder);
  const drawEntry = useStore((s) => s.features.draw.drawEntry[draw.id]);
  const resolvedArtist = product.productArtist?.[0] as ResolvedRelatedArtist;
  const { loading, setupComplete, setupController } = usePreAuthDrawCtx();
  const labels = useDrawButtonLabels(buttonLocation, isPreOrder);

  const subscriptions = buildSubscriptions({
    'artists': product.productArtist?.map(
      (a: ResolvedRelatedArtist) => a.artist,
    ),
    'isPreOrder': isPreOrder,
    'productId': draw.productId,
    'productRef': productRef,
    'productTitle': product.title,
  });

  const { isSubscribedAll, refetch } = useIsSubscribed(
    subscriptions.map(({ objectId, type }) => ({ objectId, type })),
  );

  const startDrawFlow = useCallback(async () => {
    if (setupComplete === false) {
      await setupController();
    }
    startFlow(flowConfig);
  }, [setupComplete, setupController, startFlow, flowConfig]);

  const enterDraw = useCallback(() => {
    if (isLoggedIn && isSubscribedAll) {
      startDrawFlow();
      return;
    }
    authenticateAndSubscribe({
      authScreenThumbnail: product.thumbnail.filename,
      authScreenTitle: isPreOrder
        ? t('authentication:dynamicTitle.preorder')
        : t('authentication:dynamicTitle.draw'),
      analyticsProperties: {
        'artist': resolvedArtist.artist.name,
        'artist name': resolvedArtist.artist.name,
        'button_location': 'product page',
        'product_id': draw.productId,
        'product_status': 'coming soon',
        'product_title': product.title,
        'source': isPreOrder
          ? AuthEntryPoint.PreOrder
          : AuthEntryPoint.DrawEntry,
      },
      hideSuccessScreen: true,
      onFlowComplete: () => {
        setTimeout(() => {
          startDrawFlow();
          refetch();
        }, 150);
      },
      optInGeneral: false,
      subscriptions,
    });

    if (isLoggedIn) {
      startDrawFlow();
    }
  }, [
    authenticateAndSubscribe,
    draw.productId,
    isLoggedIn,
    isPreOrder,
    isSubscribedAll,
    product.thumbnail.filename,
    product.title,
    refetch,
    resolvedArtist.artist.name,
    startDrawFlow,
    subscriptions,
    t,
  ]);

  useEffect(() => {
    if (hasCheckoutParam && buttonLocation === 'mainCta') {
      enterDraw();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonLocation, hasCheckoutParam]);

  if (isLoggedIn && (drawEntry?.entered || draw.accountIsInDraw)) {
    return (
      <Button
        disabled
        fullWidth
        icon="ic_check"
        label={t(labels.success)}
        size="lg"
        variant="positive"
      />
    );
  }

  return (
    <Button
      disabled={isPast(new Date(draw.closeAt))}
      fullWidth
      label={t(labels.normal)}
      loading={loading}
      onClick={() => {
        onClick?.();
        emitter(isPreOrder ? 'pre-order' : 'draw entry', 'clicked', {
          'product title': product.title,
          'artist name': resolvedArtist?.artist.name || '',
          'product price':
            product.pricesAndAvailability.pricesAndCurrencies.find(
              (price) => price.currency === DEFAULT_CURRENCY_CODE,
            )?.price || 'unknown',
          'product can be framed': Boolean(
            purchasingController.frameOptions?.length,
          ),
          'product frame upgrade available': Boolean(
            purchasingController.frameOptions,
          ),
          'product frame chosen': Boolean(purchasingController.frame),
        });
        enterDraw();
      }}
      size={size}
      variant="primary"
    />
  );
};
